import * as THREE from 'three';

/**
 * All Deepwake textures are drawn at load on canvas: hull plating, faction
 * livery, grime, emissive windows/engines. Deterministic per (class, faction)
 * so every client paints identical ships.
 */

export interface FactionPalette {
  hull: string;
  hullDark: string;
  livery: string;
  liverySecondary: string;
  glow: string;
}

export const FACTION_PALETTES: Record<string, FactionPalette> = {
  'Provenance Combine': { hull: '#8d9299', hullDark: '#5b6066', livery: '#c8862a', liverySecondary: '#3b3f45', glow: '#ffc46b' },
  'Tidemark Compact': { hull: '#7d8894', hullDark: '#4c545e', livery: '#2f9e8f', liverySecondary: '#1d3a3f', glow: '#7bf5e0' },
  'The Vigil': { hull: '#9a94a8', hullDark: '#5e5a6b', livery: '#7c4dc4', liverySecondary: '#2e2440', glow: '#c9a7ff' },
  Unbound: { hull: '#6f6a66', hullDark: '#403c39', livery: '#b8452e', liverySecondary: '#2b2523', glow: '#ff8c5a' },
  Waker: { hull: '#848b93', hullDark: '#51565c', livery: '#4d8fc4', liverySecondary: '#25313c', glow: '#9fd4ff' },
};

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export interface HullMaps {
  map: THREE.CanvasTexture;
  emissiveMap: THREE.CanvasTexture;
}

const hullCache = new Map<string, HullMaps>();

/**
 * Paint a hull sheet: base plate color, panel lines, a livery band, patched
 * plates, grime streaks, rivet dots — plus an emissive sheet with window
 * rows and vents that lines up with the plating grid.
 */
export function paintHull(classKey: string, faction: string): HullMaps {
  const cacheKey = `${classKey}|${faction}`;
  const hit = hullCache.get(cacheKey);
  if (hit) return hit;

  const pal = FACTION_PALETTES[faction] ?? FACTION_PALETTES.Waker;
  const rand = mulberry32(hashStr(cacheKey));
  const size = 512;

  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const e = document.createElement('canvas');
  e.width = e.height = size;
  const eg = e.getContext('2d')!;

  // Base hull
  g.fillStyle = pal.hull;
  g.fillRect(0, 0, size, size);
  eg.fillStyle = '#000';
  eg.fillRect(0, 0, size, size);

  // Large plates with slight tone variance
  const plateRows = 6 + Math.floor(rand() * 3);
  const plateCols = 6 + Math.floor(rand() * 3);
  const ph = size / plateRows;
  const pw = size / plateCols;
  for (let r = 0; r < plateRows; r++) {
    for (let col = 0; col < plateCols; col++) {
      const tone = (rand() - 0.5) * 22;
      g.fillStyle = shade(pal.hull, tone);
      g.fillRect(col * pw + 1, r * ph + 1, pw - 2, ph - 2);
      // occasional replaced plate in dark tone
      if (rand() < 0.08) {
        g.fillStyle = pal.hullDark;
        g.fillRect(col * pw + 3, r * ph + 3, pw - 6, ph - 6);
      }
    }
  }

  // Panel lines
  g.strokeStyle = 'rgba(0,0,0,0.42)';
  g.lineWidth = 2;
  for (let r = 0; r <= plateRows; r++) {
    g.beginPath();
    g.moveTo(0, r * ph);
    g.lineTo(size, r * ph);
    g.stroke();
  }
  for (let col = 0; col <= plateCols; col++) {
    g.beginPath();
    g.moveTo(col * pw, 0);
    g.lineTo(col * pw, size);
    g.stroke();
  }
  // Fine sub-panel lines
  g.strokeStyle = 'rgba(0,0,0,0.18)';
  g.lineWidth = 1;
  for (let i = 0; i < 26; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const horizontal = rand() < 0.5;
    g.beginPath();
    if (horizontal) {
      g.moveTo(x, y);
      g.lineTo(Math.min(size, x + 30 + rand() * 90), y);
    } else {
      g.moveTo(x, y);
      g.lineTo(x, Math.min(size, y + 30 + rand() * 90));
    }
    g.stroke();
  }

  // Livery band (two stripes) across the sheet
  const bandY = size * (0.18 + rand() * 0.2);
  const bandH = size * (0.07 + rand() * 0.05);
  g.fillStyle = pal.livery;
  g.globalAlpha = 0.92;
  g.fillRect(0, bandY, size, bandH);
  g.fillStyle = pal.liverySecondary;
  g.fillRect(0, bandY + bandH * 1.5, size, bandH * 0.35);
  g.globalAlpha = 1;

  // Grime streaks
  for (let i = 0; i < 46; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const len = 12 + rand() * 70;
    const grd = g.createLinearGradient(x, y, x, y + len);
    grd.addColorStop(0, 'rgba(20,18,14,0.30)');
    grd.addColorStop(1, 'rgba(20,18,14,0)');
    g.fillStyle = grd;
    g.fillRect(x, y, 1.5 + rand() * 3, len);
  }

  // Rivets
  g.fillStyle = 'rgba(0,0,0,0.35)';
  for (let r = 0; r < plateRows; r++) {
    for (let col = 0; col < plateCols; col++) {
      if (rand() < 0.5) continue;
      g.beginPath();
      g.arc(col * pw + 5, r * ph + 5, 1.4, 0, Math.PI * 2);
      g.arc(col * pw + pw - 5, r * ph + ph - 5, 1.4, 0, Math.PI * 2);
      g.fill();
    }
  }

  // Emissive: window rows aligned to plate rows
  for (let r = 0; r < plateRows; r++) {
    if (rand() < 0.35) continue;
    const y = r * ph + ph * 0.45;
    let x = rand() * 30;
    while (x < size) {
      const w = 3 + rand() * 6;
      if (rand() < 0.72) {
        eg.fillStyle = rand() < 0.9 ? pal.glow : '#fff6d8';
        eg.fillRect(x, y, w, 3);
      }
      x += w + 4 + rand() * 10;
    }
  }
  // Vent glows near the livery band
  eg.fillStyle = pal.glow;
  eg.globalAlpha = 0.55;
  for (let i = 0; i < 8; i++) {
    if (rand() < 0.4) continue;
    eg.fillRect(rand() * size, bandY + bandH + 4, 14 + rand() * 20, 2);
  }
  eg.globalAlpha = 1;

  const map = new THREE.CanvasTexture(c);
  const emissiveMap = new THREE.CanvasTexture(e);
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  emissiveMap.wrapS = emissiveMap.wrapT = THREE.RepeatWrapping;
  map.colorSpace = THREE.SRGBColorSpace;

  const out = { map, emissiveMap };
  hullCache.set(cacheKey, out);
  return out;
}

function shade(hex: string, delta: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, ((n >> 16) & 255) + delta));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 255) + delta));
  const b = Math.max(0, Math.min(255, (n & 255) + delta));
  return `rgb(${r},${g},${b})`;
}

/** Painted planet texture: banded gas giants, cratered rock, veined ice. */
export function paintPlanet(kind: 'gas' | 'rock' | 'ice', seed: number): THREE.CanvasTexture {
  const rand = mulberry32(seed >>> 0);
  const w = 512;
  const h = 256;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;

  if (kind === 'gas') {
    const hue = rand() * 360;
    for (let y = 0; y < h; y++) {
      const band = Math.sin(y * 0.05 + Math.sin(y * 0.013) * 3 + rand() * 0.08);
      const l = 26 + band * 12 + (rand() - 0.5) * 3;
      g.fillStyle = `hsl(${hue + band * 18}, ${38 + band * 10}%, ${l}%)`;
      g.fillRect(0, y, w, 1);
    }
    // storms
    for (let i = 0; i < 5; i++) {
      const x = rand() * w;
      const y = rand() * h;
      const rx = 14 + rand() * 40;
      g.fillStyle = `hsla(${hue + 30}, 55%, ${40 + rand() * 20}%, 0.5)`;
      g.beginPath();
      g.ellipse(x, y, rx, rx * 0.4, 0, 0, Math.PI * 2);
      g.fill();
    }
  } else if (kind === 'rock') {
    const hue = 18 + rand() * 30;
    g.fillStyle = `hsl(${hue}, 22%, 30%)`;
    g.fillRect(0, 0, w, h);
    for (let i = 0; i < 900; i++) {
      const x = rand() * w;
      const y = rand() * h;
      const r = rand() * rand() * 14 + 1;
      g.fillStyle = `hsla(${hue + (rand() - 0.5) * 16}, 20%, ${18 + rand() * 26}%, ${0.3 + rand() * 0.5})`;
      g.beginPath();
      g.arc(x, y, r, 0, Math.PI * 2);
      g.fill();
      if (r > 6) {
        g.strokeStyle = 'rgba(0,0,0,0.35)';
        g.beginPath();
        g.arc(x, y, r, 0, Math.PI * 2);
        g.stroke();
      }
    }
  } else {
    g.fillStyle = `hsl(${195 + rand() * 25}, 30%, 62%)`;
    g.fillRect(0, 0, w, h);
    for (let i = 0; i < 300; i++) {
      g.fillStyle = `hsla(${190 + rand() * 40}, ${20 + rand() * 30}%, ${55 + rand() * 30}%, ${0.2 + rand() * 0.3})`;
      const x = rand() * w;
      const y = rand() * h;
      g.fillRect(x, y, 20 + rand() * 90, 2 + rand() * 8);
    }
    // crevasses
    g.strokeStyle = 'rgba(30,60,90,0.5)';
    for (let i = 0; i < 40; i++) {
      g.beginPath();
      let x = rand() * w;
      let y = rand() * h;
      g.moveTo(x, y);
      for (let j = 0; j < 6; j++) {
        x += (rand() - 0.5) * 60;
        y += (rand() - 0.5) * 24;
        g.lineTo(x, y);
      }
      g.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Procedural nebula + starfield skybox (six canvas faces). */
export function paintSkybox(seed: number): THREE.CubeTexture {
  const rand = mulberry32(seed);
  const size = 1024;
  const faces: HTMLCanvasElement[] = [];

  // Nebula palette per system
  const hue1 = rand() * 360;
  const hue2 = (hue1 + 40 + rand() * 80) % 360;

  for (let f = 0; f < 6; f++) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d')!;
    g.fillStyle = '#04060c';
    g.fillRect(0, 0, size, size);

    // Nebula blobs: layered soft radial gradients
    const blobs = 8 + Math.floor(rand() * 8);
    for (let i = 0; i < blobs; i++) {
      const x = rand() * size;
      const y = rand() * size;
      const r = 90 + rand() * 320;
      const hue = rand() < 0.6 ? hue1 : hue2;
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, `hsla(${hue}, 60%, ${18 + rand() * 22}%, ${0.10 + rand() * 0.14})`);
      grd.addColorStop(1, 'hsla(0,0%,0%,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, size, size);
    }

    // Stars
    const stars = 900 + Math.floor(rand() * 600);
    for (let i = 0; i < stars; i++) {
      const x = rand() * size;
      const y = rand() * size;
      const m = rand();
      const r = m < 0.92 ? 0.6 : m < 0.99 ? 1.1 : 1.8;
      const b = 130 + Math.floor(rand() * 125);
      g.fillStyle = rand() < 0.08 ? `rgba(${b},${b * 0.82},${b * 0.72},1)` : `rgba(${b},${b},${Math.min(255, b + 20)},1)`;
      g.beginPath();
      g.arc(x, y, r, 0, Math.PI * 2);
      g.fill();
    }
    faces.push(c);
  }

  const tex = new THREE.CubeTexture(faces);
  tex.needsUpdate = true;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
